import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getMetadata, getMetadataList } from '@/services/metadata.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { QueryKey } from '@/lib/queryKeys';

export const useMetadataList = (
  params: {
    pagination: { limit: number | null; offset: number | null };
    sort: { order: string; value: string };
    queryString: string;
  },
  options?: object,
) => {
  const metadataListQueryFn = useAuthenticatedQueryFn(getMetadataList);

  const {
    data: metadataList = {
      metadata: [],
      pagination: { count: 0, limit: 0, offset: 0 },
      sort: { order: SortOrderOptions.ASC, value: SortOrderValues.CREATED_AT },
    },
    isPending,
    isFetching,
    isFetched,
    error,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.MetadataList, params.pagination, params.queryString, params.sort],
    queryFn: () => metadataListQueryFn(params),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    data: metadataList.metadata,
    pagination: metadataList.pagination,
    isFetching,
    isPending,
    isFetched,
    error,
    refetch,
  };
};

export const useMetadata = (id: string, options?: object) => {
  const metadataQueryFn = useAuthenticatedQueryFn(getMetadata);

  const {
    data: metadata,
    isPending: metadataIsPending,
    isFetching: metadataIsFetching,
    error: metadataError,
    refetch: refetchMetadata,
  } = useQuery({
    queryKey: [QueryKey.Metadata, id],
    queryFn: () => metadataQueryFn({ id }),
    ...options,
  });

  return {
    metadata,
    metadataIsFetching,
    metadataIsPending,
    metadataError,
    refetchMetadata,
  };
};
